import React from 'react';
import { DocumentIcon } from '@heroicons/react/24/outline';

import { Button } from '@oysterjs/ui/button';
import {
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
  MountableDialog
} from '@oysterjs/ui/dialog';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { AutoResizeTextarea } from '@oysterjs/ui/textarea';
import { getStaticsBaseUrl } from '@oysterjs/core/config';

import { Action, Resource } from '~/api/graphql';
import { FileInput } from '~/components/file';
import { PermissionNotice, usePermissions } from '~/components/permissions';
import { VisibilitySplitButton } from '~/components/visibility/visibility-popover';
import { useVisibilityEditAllowed } from '~/components/visibility/use-visibility-edit-allowed';
import type {
  CustomFormEditState,
  CustomFormEditStatePatch
} from '~/components/library/hooks/use-custom-form-edit-state';

const UPDATE_CUSTOM_FORM = [{ resource: Resource.CustomForm, action: Action.Update }];

/**
 * Edit dialog for a single custom form. Holds title/fill-prompt/ingestion
 * guidance/PDF replacement inputs; visibility is managed via the split
 * save button when the current user is allowed to edit visibility. The
 * `saving` flag is provided by the caller (typically derived from the
 * router's navigation state) so this component stays a pure renderer.
 */
export function CustomFormEditDialog(props: {
  editState: CustomFormEditState;
  saving: boolean;
  onSave: () => void;
  onClose: () => void;
  onEditStateChange: (patch: CustomFormEditStatePatch) => void;
}) {
  const { editState, saving } = props;
  const [replacementMode, setReplacementMode] = React.useState(false);
  const [titleSubmitAttempted, setTitleSubmitAttempted] = React.useState(false);
  const titleInvalid = titleSubmitAttempted && !editState.title.trim();
  const visibilityEditAllowed = useVisibilityEditAllowed(editState.form.createdBy?.id);
  const canEdit = usePermissions().hasAllPermissions(UPDATE_CUSTOM_FORM);

  const handleSaveClick = () => {
    if (!editState.title.trim()) {
      setTitleSubmitAttempted(true);
      return;
    }
    props.onSave();
  };

  return (
    <MountableDialog onClose={props.onClose} size="xl">
      <DialogTitle>
        {canEdit ? 'Edit' : 'View'} {editState.form.title}
      </DialogTitle>
      <DialogDescription>
        Update the form title, fill-time instructions, or setup guidance. Changes to setup guidance
        or the PDF may trigger reprocessing before the form is ready again.
      </DialogDescription>
      <DialogBody>
        {!canEdit && (
          <PermissionNotice className="mb-6">
            You can view this form but not change it.
          </PermissionNotice>
        )}
        <Fieldset disabled={!canEdit} className="min-w-0">
          <FieldGroup>
            {editState.form.error && (
              <div className="mt-0 rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-300">
                <strong>Processing error:</strong> {editState.form.error}
              </div>
            )}
            <Field>
              <Label>Title</Label>
              <Description>A short name for this form in your library.</Description>
              <Input
                invalid={titleInvalid}
                value={editState.title}
                onChange={(e) => {
                  const v = e.currentTarget.value;
                  props.onEditStateChange({ title: v });
                  if (v.trim()) setTitleSubmitAttempted(false);
                }}
                onBlur={() => {
                  if (!editState.title.trim()) setTitleSubmitAttempted(true);
                }}
              />
              {titleInvalid && <ErrorMessage>Enter a title for this form.</ErrorMessage>}
            </Field>

            <Field>
              <Label>Filling instructions</Label>
              <Description>
                Guidance Cara sees every time this form is filled. Use for form-specific
                instructions (e.g. &quot;Use effective date from the declarations&quot; or
                &quot;Leave blank if N/A&quot;).
              </Description>
              <AutoResizeTextarea
                value={editState.fillPrompt}
                onChange={(e) => props.onEditStateChange({ fillPrompt: e.currentTarget.value })}
                rows={4}
                placeholder="Example: Use annual totals for payroll and sales. Leave optional fields blank when not applicable."
              />
            </Field>

            <Field>
              <Label>Setup guidance</Label>
              <Description>
                Extra context for ambiguous fields when the form is prepared. Leave unchanged unless
                you need to clarify how Cara should interpret specific fields. Note: changing setup
                guidance or uploading a new PDF may cause the form to be reprocessed; it will show
                as Processing until ready again.
              </Description>
              <AutoResizeTextarea
                value={editState.ingestionGuidance}
                onChange={(e) =>
                  props.onEditStateChange({ ingestionGuidance: e.currentTarget.value })
                }
                rows={4}
                placeholder="Example: Treat prior claims questions as account-wide. For payroll, use annual totals."
              />
            </Field>

            <Field>
              <Label>Template PDF</Label>
              <Description>
                {replacementMode ? (
                  <>
                    Upload the replacement PDF. To remove an unwanted field, including one Cara
                    added automatically, type [[remove]] as the field&apos;s entire value before
                    uploading.
                  </>
                ) : (
                  <>
                    Download the PDF on file to review its fields, including any Cara added
                    automatically. To remove an unwanted field, type [[remove]] as its entire value
                    and upload the PDF again. You can also replace the PDF if the carrier updated
                    the form. Keep the current PDF when you are only changing title or guidance.
                  </>
                )}
              </Description>
              {!replacementMode ? (
                <div data-slot="control" className="mt-2 flex flex-row gap-4 overflow-hidden">
                  <div className="flex flex-row gap-2 min-w-0 overflow-hidden">
                    <DocumentIcon className="size-10 text-neutral-400 dark:text-neutral-500 flex-shrink-0" />
                    <div className="flex flex-col min-w-0 overflow-hidden gap-1">
                      <div
                        className="text-sm text-neutral-700 truncate dark:text-neutral-200"
                        title={editState.form.templateFile.name}
                      >
                        {editState.form.templateFile.name}
                      </div>
                      <div className="flex flex-col gap-1 sm:flex-row sm:gap-4 sm:items-center">
                        <a
                          href={`${getStaticsBaseUrl()}/agency/doc/${editState.form.templateFile.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm text-primary-500 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-200 hover:underline cursor-pointer shrink-0"
                        >
                          Download PDF
                        </a>
                        <button
                          type="button"
                          onClick={() => {
                            setReplacementMode(true);
                            props.onEditStateChange({ replacementFiles: [] });
                          }}
                          className="text-sm text-primary-500 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-200 hover:underline cursor-pointer text-left shrink-0"
                        >
                          Replace PDF
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div data-slot="control" className="mt-2 flex flex-col gap-3">
                  <FileInput
                    multiple={false}
                    onChange={(change) =>
                      props.onEditStateChange({ replacementFiles: change.files })
                    }
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setReplacementMode(false);
                      props.onEditStateChange({ replacementFiles: [] });
                    }}
                    className="text-sm text-primary-500 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-200 hover:underline cursor-pointer text-left self-start"
                  >
                    Keep current PDF
                  </button>
                </div>
              )}
            </Field>
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" onClick={props.onClose}>
          Cancel
        </Button>
        {!canEdit ? null : visibilityEditAllowed ? (
          <VisibilitySplitButton
            color="sky"
            onClick={handleSaveClick}
            value={editState.visibility}
            onChange={(next) => props.onEditStateChange({ visibility: next })}
            loading={saving}
            disabled={!editState.title.trim() || saving}
            matchDisabledAppearance={!editState.title.trim() || saving}
          >
            Save
          </VisibilitySplitButton>
        ) : (
          <Button
            color="sky"
            onClick={handleSaveClick}
            loading={saving}
            disabled={!editState.title.trim() || saving}
          >
            Save
          </Button>
        )}
      </DialogActions>
    </MountableDialog>
  );
}
